import '../assets/Exit.css';
import { defineComponent, ref, useTemplateRef, computed, onUpdated, onMounted, openBlock, createElementBlock, unref, createBlock, resolveDynamicComponent, mergeProps } from 'vue';
import { o as onKeyStroke } from './index.Cp00ESn6.js';
import { a as ToolLayoutKind, b as ToolKind, A as AgentActionKind } from './maps.nxkzRTum.js';
import { u as useSteps, h as hasModuleWithSteps } from './Text.vue_vue_type_style_index_0_lang.QyjSXreB.js';
import { n as createStore, u as updateStore } from './useStore.DAPvMbcE.js';
import { r as resetModal } from './useModal.CtQMkYkB.js';
import { _ as _sfc_main$1, C as CustomImage, a as _sfc_main$2, b as _sfc_main$3, c as _sfc_main$4, N as NotificationTab, d as _sfc_main$5, e as _sfc_main$6, f as _sfc_main$7, g as _sfc_main$8, h as _sfc_main$9, i as _sfc_main$a } from './StickyImage.vue_vue_type_style_index_0_lang.5VM6AFR6.js';
import { p as parseToolData } from './parseToolData.C-LjimTE.js';

function isElementInShadowDom(el) {
  return el.getRootNode() instanceof ShadowRoot;
}
function getRootDocument(el) {
  return isElementInShadowDom(el) ? el.getRootNode() : document;
}
function focusOnEnter(container) {
  if (!container) return;
  const focusable = container.querySelector(
    'input, select, textarea, button, a[href], [tabindex]:not([tabindex="-1"])'
  );
  if (focusable instanceof HTMLElement && focusable.offsetParent !== null && focusable.getBoundingClientRect().width > 0) {
    focusable.focus();
  } else {
    const title = container.querySelector('[tabindex="-1"]');
    if (title instanceof HTMLElement && title.offsetParent !== null && title.getBoundingClientRect().width > 0) {
      title.focus();
    } else {
      container.setAttribute("tabindex", "0");
      container.focus();
    }
  }
}
function trapFocus(container) {
  if (!container) return () => {
  };
  const element = "$el" in container ? container.$el : container;
  const rootDocument = getRootDocument(element);
  const trapFocusEvent = (e) => {
    if (e.key !== "Tab") return;
    const focusableElements = Array.from(
      element.querySelectorAll(
        'input, select, textarea, button, a[href], [tabindex]:not([tabindex="-1"])'
      )
    ).filter((el) => !el.hasAttribute("disabled") && el.offsetParent !== null);
    const first = focusableElements[0];
    const last = focusableElements[focusableElements.length - 1];
    const active = rootDocument.activeElement;
    if (e.shiftKey) {
      if (active === first) {
        e.preventDefault();
        last.focus();
      }
    } else if (active === last) {
      e.preventDefault();
      first.focus();
    }
  };
  element.addEventListener("keydown", trapFocusEvent);
  return trapFocusEvent;
}

const _hoisted_1 = ["data-builder-mode", "data-device", "data-content-alignment", "data-layout-kind", "data-text-direction", "aria-label"];
const _sfc_main = /* @__PURE__ */ defineComponent({
  __name: "Popup",
  props: {
    tool: {},
    context: {}
  },
  emits: [
    "hidden"
  ],
  setup(__props, { expose: __expose, emit: __emit }) {
    const layoutComponents = {
      [ToolLayoutKind.Circular]: _sfc_main$1,
      [ToolLayoutKind.CustomImage]: CustomImage,
      [ToolLayoutKind.Fullscreen]: _sfc_main$2,
      [ToolLayoutKind.FullscreenMini]: _sfc_main$3,
      [ToolLayoutKind.LowerNotification]: _sfc_main$4,
      [ToolLayoutKind.NotificationTab]: NotificationTab,
      [ToolLayoutKind.Sidebar]: _sfc_main$5,
      [ToolLayoutKind.SideGradient]: _sfc_main$6,
      [ToolLayoutKind.Square]: _sfc_main$7,
      [ToolLayoutKind.SquareImage]: _sfc_main$8,
      [ToolLayoutKind.Sticky]: _sfc_main$9,
      [ToolLayoutKind.StickyImage]: _sfc_main$a
    };
    const props = __props;
    const emit = __emit;
    const store = createStore(parseToolData({
      ...props,
      toolKind: ToolKind.Layer
    }));
    const componentRef = ref();
    const rootEl = useTemplateRef("rootEl");
    let trapFocusEvent;
    const layoutProps = computed(() => {
      const {
        language,
        deviceViewport
      } = props.context;
      const {
        content: {
          modules
        },
        settings: {
          layout
        }
      } = props.tool;
      const steps = useSteps(modules, props.context.builderMode);
      const hasSteps = hasModuleWithSteps(modules);
      return {
        language,
        device: deviceViewport,
        steps,
        hasSteps,
        layout
      };
    });
    const isBlockingLayout = computed(() => layoutProps.value.layout.type !== ToolLayoutKind.Sticky && layoutProps.value.layout.type !== ToolLayoutKind.StickyImage && layoutProps.value.layout.type !== ToolLayoutKind.NotificationTab && layoutProps.value.layout.type !== ToolLayoutKind.LowerNotification && layoutProps.value.layout.type !== ToolLayoutKind.SideGradient);
    const asyncComponent = computed(() => {
      if (!props.tool.settings.layout) {
        return null;
      }
      const layoutKind = props.tool.settings.layout?.type;
      return layoutComponents[layoutKind];
    });
    function show() {
      componentRef.value?.show();
    }
    function hide() {
      componentRef.value?.hide();
    }
    onUpdated(() => {
      updateStore(parseToolData({
        ...props,
        toolKind: ToolKind.Layer
      }));
    });
    onMounted(() => {
      store.handlers.callHandler(AgentActionKind.Show);
    });
    function onHideLayout() {
      store.handlers.reset();
      resetModal();
      emit("hidden");
      if (!rootEl.value) return;
      rootEl.value.removeEventListener("keydown", trapFocusEvent);
    }
    function onEnter() {
      if (!props.context.builderMode && !!rootEl.value) {
        if (isBlockingLayout.value) {
          focusOnEnter(rootEl.value);
          trapFocusEvent = trapFocus(rootEl.value);
        }
      }
    }
    onKeyStroke("Escape", () => {
      emit("hidden");
      hide();
    }, { target: rootEl });
    __expose({
      show,
      hide
    });
    return (_ctx, _cache) => {
      return openBlock(), createElementBlock("div", {
        ref_key: "rootEl",
        ref: rootEl,
        class: "thn--tool thn--tool-popup",
        role: "dialog",
        "data-builder-mode": _ctx.context.builderMode,
        "data-device": _ctx.context.deviceViewport,
        "data-content-alignment": _ctx.tool.settings.layout?.contentAlignment,
        "data-layout-kind": _ctx.tool.settings.layout?.type,
        "data-text-direction": unref(store).rtl ? "rtl" : "ltr",
        "aria-modal": "true",
        "aria-label": props.tool.name
      }, [
        (openBlock(), createBlock(resolveDynamicComponent(asyncComponent.value), mergeProps({
          ref_key: "componentRef",
          ref: componentRef
        }, layoutProps.value, {
          "modal-root-container": _ctx.context.modalRootContainer,
          onAfterLeave: onHideLayout,
          onAfterEnter: onEnter
        }), null, 16, ["modal-root-container"]))
      ], 8, _hoisted_1);
    };
  }
});

export { _sfc_main as _ };
//# sourceMappingURL=Popup.vue_vue_type_style_index_0_lang.B2Skg71C.js.map
